// Pure logic for the case History feed (no React, no DOM).
//
// The feed is a Salesforce-style activity timeline: a summary panel, a control
// row, then rows grouped under "UPCOMING & OVERDUE", one header per recent
// calendar month, and "OLDER". Classification / label / date resolution are
// reused from the activity strip's utils so both surfaces agree on what a
// "touch" is.

import dayjs from "dayjs";
import {
  activityDate,
  classifyActivity,
  displayLabel,
  formatRelative,
  IGNORED_ACTIVITY_TYPES,
} from "../ActivityTimeline/timelineUtils";
import { TouchWeight } from "../ActivityTimeline/types";

/* ------------------------------------------------------------------ *
 * Categories (the "All activity types" menu)
 * ------------------------------------------------------------------ */

export type HistoryCategory =
  | "contact"
  | "referral"
  | "survey"
  | "task"
  | "system"
  | "bulk";

export type HistoryFilter = "all" | HistoryCategory;

/**
 * Menu entries. Only categories that `isHistoryType` can still produce are
 * offered — files, action items, reminders and lifecycle rows never reach the
 * feed, so "task" / "system" stay valid categories (icons, counts) but are not
 * listed. "bulk" (event invitations + broadcasts) only appears while its
 * show/hide checkbox is on, and the component appends it then.
 */
export const HISTORY_FILTERS: HistoryFilter[] = [
  "all",
  "contact",
  "referral",
  "survey",
];

export const FILTER_LABELS: { [key in HistoryFilter]: string } = {
  all: "All",
  contact: "Contact",
  referral: "Referrals",
  survey: "Surveys",
  task: "Files & tasks",
  system: "System",
  bulk: "Event/broadcast emails",
};

/** The control-row button label while no category is selected. */
export const ALL_TYPES_LABEL = "All activity types";

const FILE_TYPES: ReadonlySet<string> = new Set(["Upload File", "Delete File"]);

/**
 * Which menu entry an activity type belongs to. Order matters:
 *  - anything with "Survey" in it is a survey (so "Referral Survey Email" and
 *    "Client Referral Survey" land under Surveys, not Referrals),
 *  - then anything with "Referral" in it,
 *  - then file / action-item / reminder bookkeeping,
 *  - then whatever the strip already calls a system event (this also swallows
 *    unknown types, exactly like `classifyActivity`),
 *  - everything left over is real contact work.
 */
export const categorize = (activityType: string): HistoryCategory => {
  const type = (activityType || "").trim();
  if (!type) return "system";
  if (/survey/i.test(type)) return "survey";
  if (/referral/i.test(type)) return "referral";
  if (
    FILE_TYPES.has(type) ||
    /^Action Item/i.test(type) ||
    /^Reminder/i.test(type)
  ) {
    return "task";
  }
  if (classifyActivity(type) === "system") return "system";
  return "contact";
};

/* ------------------------------------------------------------------ *
 * Row model
 * ------------------------------------------------------------------ */

export interface HistoryReferralInfo {
  /** Partner / provider name when we can resolve one. */
  partner: string;
  /** Humanized pipeline status ("In Progress"). */
  status: string;
}

/** The line-icon glyph a row draws on its left. */
export type HistoryIcon =
  | "phone"
  | "star"
  | "ref"
  | "doc"
  | "check"
  | "bell"
  | "dot"
  | "mail";

export interface HistoryRow {
  id: string;
  date: Date;
  type: string;
  /** displayLabel(activity_type) */
  label: string;
  weight: TouchWeight;
  category: HistoryCategory;
  /** One-line preview, "" when there is nothing worth showing. */
  excerpt: string;
  /** Full activity_note, line breaks preserved. */
  note: string;
  method: string;
  fileName: string;
  referral: HistoryReferralInfo | null;
  contactTime: number | null;
  prepTime: number | null;
  travelTime: number | null;
  userId: string;
  /** false for the Case page's ignored types — no Edit link. */
  editable: boolean;
  /** System-weight rows render as a muted one-liner (no expand / Edit). */
  minor: boolean;
}

const IGNORED_SET: ReadonlySet<string> = new Set(IGNORED_ACTIVITY_TYPES);

const PIPELINE_STATUS_LABELS: { [key: string]: string } = {
  referred: "Referred",
  appointment_scheduled: "Appointment Scheduled",
  in_progress: "In Progress",
  completed: "Completed",
  unsuccessful: "Unsuccessful",
  follow_up_needed: "Follow-Up Needed",
};

/** "in_progress" → "In Progress"; unknown values are title-cased on the fly. */
export const humanizePipelineStatus = (status: unknown): string => {
  const key = typeof status === "string" ? status.trim() : "";
  if (!key) return "";
  if (PIPELINE_STATUS_LABELS[key]) return PIPELINE_STATUS_LABELS[key];
  return key
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const oneLine = (value: unknown): string =>
  typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const toCount = (value: unknown): number | null => {
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * The referral rows carried by the case page are sometimes just referral ids
 * (Case.tsx keeps a list of ids) and sometimes full pipeline rows
 * (`GET /referral/pipeline`). Index whatever has an `_id` and ignore the rest.
 */
export const indexReferrals = (referrals: any[]): { [id: string]: any } => {
  const map: { [id: string]: any } = {};
  (Array.isArray(referrals) ? referrals : []).forEach((referral) => {
    if (!referral || typeof referral !== "object") return;
    const id = referral._id ?? referral.referral_id;
    if (id === null || id === undefined || id === "") return;
    map[String(id)] = referral;
  });
  return map;
};

/** Partner name as the pipeline endpoint / referral doc spells it. */
const partnerName = (referral: any): string => {
  if (!referral) return "";
  const candidates = [
    referral.referred_to,
    referral.partner_name,
    referral.provider_name,
    referral.resource_title,
    referral.orgName,
    referral.title,
    referral.name,
  ];
  const found = candidates.find((value) => text(value) !== "");
  return text(found);
};

const referralInfo = (
  activity: any,
  byId: { [id: string]: any },
): HistoryReferralInfo | null => {
  const refId = activity?.referral_id;
  if (refId === null || refId === undefined || refId === "") return null;
  const referral = byId[String(refId)];
  if (!referral) return null;
  const partner = partnerName(referral);
  const status = humanizePipelineStatus(referral.pipeline_status);
  if (!partner && !status) return null;
  return { partner, status };
};

/** CaseFileManager reads the display name off activity_change.originalName. */
const fileNameOf = (activity: any): string => {
  const change = activity?.activity_change;
  if (!change || typeof change !== "object") return "";
  return text(change.originalName) || text(change.storageName);
};

export const buildRow = (activity: any, byId: { [id: string]: any }): HistoryRow => {
  const type = String(activity?.activity_type ?? "");
  const weight = classifyActivity(type);
  const note = typeof activity?.activity_note === "string" ? activity.activity_note : "";
  const referral = referralInfo(activity, byId);
  const fileName = FILE_TYPES.has(type) ? fileNameOf(activity) : "";

  // First non-empty wins: note → referral partner/status → file name.
  let excerpt = oneLine(note);
  if (!excerpt && referral) {
    excerpt = [referral.partner, referral.status].filter(Boolean).join(" · ");
  }
  if (!excerpt && fileName) excerpt = fileName;

  return {
    id: String(activity?._id ?? ""),
    date: activityDate(activity),
    type,
    label: displayLabel(type),
    weight,
    category: categorize(type),
    excerpt,
    note: note.trim(),
    method: text(activity?.activity_method),
    fileName,
    referral,
    contactTime: toCount(activity?.activity_contact_time),
    prepTime: toCount(activity?.activity_prep_time),
    travelTime: toCount(activity?.activity_travel_time),
    userId: activity?.user_id ? String(activity.user_id) : "",
    editable: !IGNORED_SET.has(type),
    minor: weight === "system",
  };
};

const isValidDate = (d: Date | null | undefined): d is Date =>
  !!d && !Number.isNaN(d.getTime());

/* ------------------------------------------------------------------ *
 * What the feed shows
 * ------------------------------------------------------------------ */

/** The logged-interaction types (`Case/Interaction.tsx` `interaction_types`). */
const INTERACTION_TYPES: ReadonlySet<string> = new Set([
  "Attempted Contact",
  "B2B Approval",
  "B2B Prerequisites",
  "B2B Request Incomplete/Denied",
  "Client Interaction",
  "Community Interaction",
  "Follow-up",
  "Scheduled Visit",
]);

/**
 * The feed is deliberately narrow: logged interactions and anything
 * referral-related (Referral, Referral Notice, Provider Referral *, Referral
 * Status Updated, referral / B2B survey emails and responses). Everything else
 * — "(edit)" bookkeeping, Action Items / the retired Business Action Plan,
 * case-flow steps, files, reminders, lifecycle events, Event Invitation
 * mass-mail — is hidden.
 */
export const isHistoryType = (activityType: unknown): boolean => {
  const type = typeof activityType === "string" ? activityType.trim() : "";
  if (!type) return false;
  if (/\(edit\)$/i.test(type)) return false;
  if (/action item|action plan/i.test(type)) return false;
  if (INTERACTION_TYPES.has(type)) return true;
  return /referral|b2b/i.test(type);
};

/**
 * Bulk mail — legacy event invitations (`massiveSendEmails` logs one "Event
 * Invitation" activity per firm) — hidden unless the "Event/broadcast emails"
 * checkbox is on. Broadcast-builder sends write no activity at all; they come
 * from `GET /broadcast/case-history` via `buildBroadcastRows`.
 */
export const BULK_EMAIL_ACTIVITY_TYPES: ReadonlySet<string> = new Set([
  "Event Invitation",
]);

export const isBulkEmailType = (activityType: unknown): boolean =>
  typeof activityType === "string" &&
  BULK_EMAIL_ACTIVITY_TYPES.has(activityType.trim());

/** Newest first, id as the tiebreak so the order is stable. */
const byNewest = (a: HistoryRow, b: HistoryRow): number => {
  const delta = b.date.getTime() - a.date.getTime();
  if (delta !== 0) return delta;
  return a.id.localeCompare(b.id);
};

export interface BuildRowsOptions {
  /** Include "Event Invitation" activities (the fourth checkbox). */
  bulkEmails?: boolean;
  /** `email_id` → subject, from `GET /broadcast/case-history` `invitations`. */
  invitationSubjects?: { [emailId: string]: string };
}

/**
 * A bulk-mail row: counted under "bulk", never a touch (system weight, so the
 * stats line and staleness ignore it), no Edit link, but — unlike other
 * system rows — it keeps its detail (subject / delivery status).
 */
const asBulkRow = (row: HistoryRow): HistoryRow => ({
  ...row,
  category: "bulk",
  weight: "system",
  minor: false,
  editable: false,
});

/** Rows for the feed, newest first; deleted, hidden-type + undated rows dropped. */
export const buildRows = (
  activities: any[],
  referrals: any[] = [],
  options: BuildRowsOptions = {},
): HistoryRow[] => {
  const byId = indexReferrals(referrals);
  const subjects = options.invitationSubjects || {};
  return (Array.isArray(activities) ? activities : [])
    .filter(
      (a) =>
        a &&
        a.status !== "deleted" &&
        (isHistoryType(a.activity_type) ||
          (!!options.bulkEmails && isBulkEmailType(a.activity_type))),
    )
    .map((a) => {
      const row = buildRow(a, byId);
      if (!isBulkEmailType(row.type)) return row;
      const subject = text(subjects[String(a.email_id ?? "")]);
      return asBulkRow({ ...row, excerpt: subject || row.excerpt });
    })
    .filter((row) => isValidDate(row.date))
    .sort(byNewest);
};

/** SendGrid outcome first, then the send accounting. */
const BROADCAST_STATUS_LABELS: { [key: string]: string } = {
  delivered: "Delivered",
  bounced: "Bounced",
  dropped: "Dropped",
  blocked: "Blocked",
  spam_report: "Marked as spam",
  sent: "Sent",
  error: "Failed",
  not_attempted: "Not sent",
  skipped: "Skipped",
};

export const broadcastStatusLabel = (record: any): string => {
  const delivery = text(record?.delivery_status);
  if (delivery) return BROADCAST_STATUS_LABELS[delivery] || humanizePipelineStatus(delivery);
  const status = text(record?.status);
  if (!status) return "";
  const label = BROADCAST_STATUS_LABELS[status] || humanizePipelineStatus(status);
  const reason = text(record?.reason);
  return status === "skipped" && reason
    ? `${label} (${humanizePipelineStatus(reason).toLowerCase()})`
    : label;
};

/**
 * `GET /broadcast/case-history` `broadcasts` → feed rows. One row per paper
 * trail row (one per broadcast the firm was in); the subject is the detail and
 * the delivery outcome + address go in the expandable body.
 */
export const buildBroadcastRows = (records: any[]): HistoryRow[] =>
  (Array.isArray(records) ? records : [])
    .filter((r) => r && r._id)
    .map((r) => {
      const label =
        r.broadcastType === "event_invitation"
          ? "Event Invitation Broadcast"
          : "Broadcast Email";
      const status = broadcastStatusLabel(r);
      const emails = Array.isArray(r.emails) ? r.emails.filter(Boolean) : [];
      const note = [
        status ? `Status: ${status}` : "",
        text(r.delivery_reason),
        emails.length ? `To: ${emails.join(", ")}` : "",
        text(r.fromBFC) ? `From: ${text(r.fromBFC)}` : "",
      ]
        .filter(Boolean)
        .join("\n");
      const subject = oneLine(r.subject);
      return asBulkRow({
        id: `broadcast-${String(r._id)}`,
        date: toDate(r.date) || new Date(NaN),
        type: label,
        label,
        weight: "system",
        category: "bulk",
        excerpt: [subject, status].filter(Boolean).join(" · "),
        note,
        method: "",
        fileName: "",
        referral: null,
        contactTime: null,
        prepTime: null,
        travelTime: null,
        userId: r.from ? String(r.from) : "",
        editable: false,
        minor: false,
      });
    })
    .filter((row) => isValidDate(row.date));

/** Merge two newest-first row lists into one. */
export const mergeRows = (a: HistoryRow[], b: HistoryRow[]): HistoryRow[] =>
  b.length === 0 ? a : [...a, ...b].sort(byNewest);

/* ------------------------------------------------------------------ *
 * Row presentation — bold type, consultant initials, muted detail, left glyph
 * ------------------------------------------------------------------ */

/** "Referral · Metro SBDC", otherwise the plain activity label. */
export const rowTitle = (row: HistoryRow): string => {
  if (row.referral && row.referral.partner) {
    return `${row.label} · ${row.referral.partner}`;
  }
  return row.label;
};

/**
 * The `·`-separated tail of the second line — what follows the person's name.
 * Referrals show their pipeline status, file rows the file name, everything
 * else the first line of the note.
 */
export const rowDetail = (row: HistoryRow): string => {
  if (row.referral && row.referral.status) {
    return `Status → ${row.referral.status}`;
  }
  if (row.fileName) return row.fileName;
  return row.excerpt;
};

/** Phone for contact work, star for surveys, reply-arrow for referrals … */
export const rowIcon = (row: HistoryRow): HistoryIcon => {
  switch (row.category) {
    case "survey":
      return "star";
    case "referral":
      return "ref";
    case "system":
      return "dot";
    case "bulk":
      return "mail";
    case "task":
      return FILE_TYPES.has(row.type) ? "doc" : "check";
    default:
      return "phone";
  }
};

/* ------------------------------------------------------------------ *
 * Filtering + counts
 * ------------------------------------------------------------------ */

export type HistoryCounts = { [key in HistoryFilter]: number };

export const countByCategory = (rows: HistoryRow[]): HistoryCounts => {
  const counts: HistoryCounts = {
    all: 0,
    contact: 0,
    referral: 0,
    survey: 0,
    task: 0,
    system: 0,
    bulk: 0,
  };
  rows.forEach((row) => {
    counts.all += 1;
    counts[row.category] += 1;
  });
  return counts;
};

export const filterRows = (
  rows: HistoryRow[],
  filter: HistoryFilter,
): HistoryRow[] =>
  filter === "all" ? rows : rows.filter((row) => row.category === filter);

/* ------------------------------------------------------------------ *
 * Time window ("All time ▾")
 * ------------------------------------------------------------------ */

export type TimeWindow = "all" | "30d" | "90d" | "year";

export const TIME_WINDOWS: TimeWindow[] = ["all", "30d", "90d", "year"];

export const TIME_WINDOW_LABELS: { [key in TimeWindow]: string } = {
  all: "All time",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  year: "This year",
};

/** Rows inside the selected window; day-granular so it never depends on time. */
export const filterByWindow = (
  rows: HistoryRow[],
  window: TimeWindow,
  now: Date = new Date(),
): HistoryRow[] => {
  if (window === "all") return rows;
  const today = dayjs(now).startOf("day");
  if (window === "year") {
    return rows.filter((row) => dayjs(row.date).isSame(today, "year"));
  }
  const days = window === "30d" ? 30 : 90;
  const floor = today.subtract(days, "day");
  return rows.filter((row) => !dayjs(row.date).startOf("day").isBefore(floor));
};

/* ------------------------------------------------------------------ *
 * Month grouping
 * ------------------------------------------------------------------ */

export interface HistoryGroup {
  /** "2026-05", or "older". */
  key: string;
  /** Uppercase header text — "MAY 2026" / "OLDER". */
  label: string;
  rows: HistoryRow[];
}

export const UPCOMING_LABEL = "UPCOMING & OVERDUE";
export const OLDER_LABEL = "OLDER";

/** How many calendar months get their own header before "OLDER" takes over. */
export const MONTH_GROUP_LIMIT = 3;

const monthKey = (date: Date): string => dayjs(date).format("YYYY-MM");

const monthLabel = (date: Date): string =>
  dayjs(date).format("MMMM YYYY").toUpperCase();

/**
 * Newest-first rows → a header per calendar month for the most recent
 * `MONTH_GROUP_LIMIT` months that actually have rows, then one "OLDER"
 * bucket for everything before them.
 */
export const groupRowsByMonth = (rows: HistoryRow[]): HistoryGroup[] => {
  const months: string[] = [];
  rows.forEach((row) => {
    const key = monthKey(row.date);
    if (months.indexOf(key) === -1) months.push(key);
  });
  const named = months.slice(0, MONTH_GROUP_LIMIT);

  const groups: HistoryGroup[] = [];
  const push = (key: string, label: string, row: HistoryRow) => {
    const last = groups.length ? groups[groups.length - 1] : null;
    if (last && last.key === key) {
      last.rows.push(row);
      return;
    }
    groups.push({ key, label, rows: [row] });
  };

  rows.forEach((row) => {
    const key = monthKey(row.date);
    if (named.indexOf(key) === -1) {
      push("older", OLDER_LABEL, row);
      return;
    }
    push(key, monthLabel(row.date), row);
  });
  return groups;
};

/* ------------------------------------------------------------------ *
 * Upcoming & overdue (reminders + open Action Item tasks)
 * ------------------------------------------------------------------ */

export interface UpcomingItem {
  id: string;
  kind: "reminder" | "task";
  title: string;
  due: Date;
  /** Reminder note; "" for tasks. */
  note: string;
  /** Who it belongs to — reminder owner, or the case owner for a task. */
  ownerId: string;
}

const toDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = value instanceof Date ? value : new Date(value as any);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const DEAD_PLAN_STATUSES: ReadonlySet<string> = new Set([
  "deleted",
  "temp",
  "completed",
]);

/**
 * The two sources of "something is due on this case":
 *  - pending reminders from `GET /reminder?status=all&case_id=` (`data.reminders`)
 *  - incomplete, dated tasks on live Action Items from
 *    `GET /actionPlan?case_id=` (`data.action_plans`)
 * Sorted by due date, soonest (most overdue) first.
 */
export const buildUpcoming = (
  reminders: any[],
  plans: any[],
  caseOwnerId: string = "",
): UpcomingItem[] => {
  const items: UpcomingItem[] = [];

  (Array.isArray(reminders) ? reminders : []).forEach((reminder) => {
    if (!reminder || reminder.status !== "pending") return;
    const due = toDate(reminder.dueAt);
    if (!due) return;
    items.push({
      id: `reminder-${String(reminder._id ?? "")}`,
      kind: "reminder",
      title: text(reminder.title) || "Reminder",
      due,
      note: typeof reminder.note === "string" ? reminder.note.trim() : "",
      ownerId: reminder.user_id ? String(reminder.user_id) : "",
    });
  });

  (Array.isArray(plans) ? plans : []).forEach((plan) => {
    if (!plan) return;
    const status = text(plan.status);
    if (status && DEAD_PLAN_STATUSES.has(status)) return;
    (Array.isArray(plan.tasks) ? plan.tasks : []).forEach((task: any) => {
      if (!task || task.completed) return;
      const due = toDate(task.due_date);
      if (!due) return;
      items.push({
        id: `task-${String(plan._id ?? "")}-${String(task.task_id ?? task.title ?? "")}`,
        kind: "task",
        title: text(task.title) || text(plan.goal) || "Task",
        due,
        note: "",
        ownerId: caseOwnerId,
      });
    });
  });

  return items.sort((a, b) => {
    const delta = a.due.getTime() - b.due.getTime();
    return delta !== 0 ? delta : a.id.localeCompare(b.id);
  });
};

/* ------------------------------------------------------------------ *
 * Header stats + staleness
 * ------------------------------------------------------------------ */

export interface HistoryStats {
  /** substantive + light */
  touches: number;
  substantive: number;
  lastTouch: Date | null;
}

export const buildStats = (rows: HistoryRow[]): HistoryStats => {
  const stats: HistoryStats = { touches: 0, substantive: 0, lastTouch: null };
  rows.forEach((row) => {
    if (row.weight === "system") return;
    stats.touches += 1;
    if (row.weight === "substantive") stats.substantive += 1;
    if (!stats.lastTouch || row.date.getTime() > stats.lastTouch.getTime()) {
      stats.lastTouch = row.date;
    }
  });
  return stats;
};

export const daysSince = (date: Date, now: Date = new Date()): number =>
  dayjs(now).startOf("day").diff(dayjs(date).startOf("day"), "day");

const WEEK_PHRASE_MIN_DAYS = 14;
const WEEK_PHRASE_MAX_DAYS = 84; // beyond ~3 months weeks stop reading well

/**
 * `formatRelative` wording, except that the 2-week…3-month window reads in
 * weeks ("10 weeks ago") — the unit people use when talking about a gap in
 * client contact.
 */
export const formatGap = (date: Date, now: Date = new Date()): string => {
  const days = daysSince(date, now);
  if (days >= WEEK_PHRASE_MIN_DAYS && days < WEEK_PHRASE_MAX_DAYS) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  }
  return formatRelative(date, now);
};

/** "due in 3 days" / "due today" / "overdue 10 weeks". */
export const dueLabel = (due: Date, now: Date = new Date()): string => {
  const days = daysSince(due, now);
  if (days <= 0) return `due ${formatRelative(due, now)}`;
  if (days === 1) return "overdue 1 day";
  return `overdue ${formatGap(due, now).replace(/ ago$/, "")}`;
};

/** "Task · overdue 10 weeks" — the row's description; initials render beside it. */
export const upcomingDetail = (item: UpcomingItem, now: Date = new Date()): string =>
  [item.kind === "task" ? "Task" : "Reminder", dueLabel(item.due, now)].join(" · ");

/** Bell for reminders, ticked box for Action Item tasks. */
export const upcomingIcon = (item: UpcomingItem): HistoryIcon =>
  item.kind === "reminder" ? "bell" : "check";

/**
 * How close an open item is to its due date — drives the row colour.
 *  overdue: past due · today: due today · soon: due within `DUE_SOON_DAYS`
 *  days · later: everything further out.
 */
export type DueState = "overdue" | "today" | "soon" | "later";

export const DUE_SOON_DAYS = 3;

export const dueState = (due: Date, now: Date = new Date()): DueState => {
  const days = daysSince(due, now);
  if (days > 0) return "overdue";
  if (days === 0) return "today";
  if (days >= -DUE_SOON_DAYS) return "soon";
  return "later";
};

/* ------------------------------------------------------------------ *
 * Show / hide checkboxes — reminders, Action Item tasks, activities, and
 * bulk mail (event invitations + broadcasts; off by default — noisy)
 * ------------------------------------------------------------------ */

export interface HistoryVisibility {
  reminders: boolean;
  tasks: boolean;
  activities: boolean;
  bulkEmails: boolean;
}

export const DEFAULT_VISIBILITY: HistoryVisibility = {
  reminders: true,
  tasks: true,
  activities: true,
  bulkEmails: false,
};

export const VISIBILITY_LABELS: { [key in keyof HistoryVisibility]: string } = {
  reminders: "Reminders",
  tasks: "Action items",
  activities: "Activities",
  bulkEmails: "Event/broadcast emails",
};

/**
 * Which feed rows the checkboxes let through: bulk rows follow "Event/broadcast
 * emails", everything else follows "Activities".
 */
export const filterFeedRows = (
  rows: HistoryRow[],
  visibility: HistoryVisibility,
): HistoryRow[] =>
  rows.filter((row) =>
    row.category === "bulk" ? visibility.bulkEmails : visibility.activities,
  );

export const filterUpcoming = (
  items: UpcomingItem[],
  visibility: HistoryVisibility,
): UpcomingItem[] =>
  items.filter((item) =>
    item.kind === "reminder" ? visibility.reminders : visibility.tasks,
  );

/** "Last touch Jun 24 · 26 touches · 16 substantive" */
export const statsSummary = (
  stats: HistoryStats,
  now: Date = new Date(),
): string => {
  if (!stats.lastTouch || stats.touches === 0) return "No touches yet";
  const when = rowDateLabel(stats.lastTouch, now);
  const touches = `${stats.touches} touch${stats.touches === 1 ? "" : "es"}`;
  return `Last touch ${when} · ${touches} · ${stats.substantive} substantive`;
};

export const STALE_AFTER_DAYS = 14;

/** "No contact for 10 weeks", or null while the case is being worked. */
export const stalenessLabel = (
  stats: HistoryStats,
  now: Date = new Date(),
): string | null => {
  if (!stats.lastTouch) return null;
  const days = daysSince(stats.lastTouch, now);
  if (days < STALE_AFTER_DAYS) return null;
  const phrase = formatGap(stats.lastTouch, now).replace(/ ago$/, "");
  return `No contact for ${phrase}`;
};

/* ------------------------------------------------------------------ *
 * Row date label
 * ------------------------------------------------------------------ */

/** "Jun 24" this year, "Jun 24, 2025" otherwise. */
export function rowDateLabel(date: Date, now: Date = new Date()): string {
  return dayjs(date).isSame(dayjs(now), "year")
    ? dayjs(date).format("MMM D")
    : dayjs(date).format("MMM D, YYYY");
}

/** Rows rendered before the "Show N older" button kicks in. */
export const INITIAL_ENTRY_LIMIT = 10;
